
import React, { useEffect, useState } from 'react';
import { applyRealtimeConfig } from '@/utils/realtimeConfig';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Component that initializes realtime functionality on mount
 * This should be added to the main layout or app component
 */
const RealtimeInitializer: React.FC = () => {
  const [initialized, setInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [channelActive, setChannelActive] = useState(false);
  
  // Setup realtime config
  useEffect(() => {
    const setupRealtime = async () => {
      // Don't try again if already initialized or max attempts reached
      if (initialized || isLoading || attempts >= 3) return;
      
      setIsLoading(true);
      console.log(`[Realtime] Initializing realtime configuration... (attempt ${attempts + 1})`);
      
      try {
        const result = await applyRealtimeConfig();
        
        if (result.success) {
          console.log(`[Realtime] ✅ Configuration initialized successfully:`, result);
          setInitialized(true);
          toast.success('Realtime configuration initialized successfully');
        } else {
          console.error(`[Realtime] ❌ Failed to initialize configuration:`, result.message);
          // Increment attempt counter
          setAttempts(prev => prev + 1);
        }
      } catch (err) {
        console.error('[Realtime] ❌ Exception during initialization:', err);
        // Increment attempt counter
        setAttempts(prev => prev + 1);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Initial setup with delay to ensure other components are loaded
    const timeoutId = setTimeout(() => {
      setupRealtime();
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [initialized, isLoading, attempts]);
  
  // Retry with increasing backoff if needed
  useEffect(() => {
    if (attempts > 0 && attempts < 3 && !initialized && !isLoading) {
      const backoffTime = Math.pow(2, attempts) * 1000; // Exponential backoff
      console.log(`[Realtime] Scheduling retry attempt ${attempts + 1} in ${backoffTime}ms`);
      
      const timeoutId = setTimeout(() => {
        console.log(`[Realtime] Retrying configuration (attempt ${attempts + 1})`);
        setIsLoading(false); // Reset loading state to allow retry
      }, backoffTime);
      
      return () => clearTimeout(timeoutId);
    }
  }, [attempts, initialized, isLoading]);

  // Create a test channel to verify if realtime is actually working
  useEffect(() => {
    if (!initialized) return;

    // Create a test channel to verify connectivity
    console.log("[Realtime] Setting up test channel to verify realtime connection");
    
    const channel = supabase
      .channel('realtime-test')
      .on(
        'presence',
        { event: 'sync' },
        () => {
          console.log('[Realtime] ✅ Test channel synced successfully');
          setChannelActive(true);
        }
      )
      .subscribe((status) => {
        console.log(`[Realtime] Test channel status: ${status}`);
        
        if (status === 'SUBSCRIBED') {
          console.log('[Realtime] ✅ Test channel subscribed successfully');
          setChannelActive(true);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Realtime] ❌ Error connecting test channel');
          setChannelActive(false);
        }
      });
      
    return () => {
      console.log('[Realtime] Removing test channel');
      supabase.removeChannel(channel);
    };
  }, [initialized]);

  // Log the current realtime status periodically
  useEffect(() => {
    if (!initialized) return;
    
    const intervalId = setInterval(() => {
      const currentChannels = supabase.getChannels();
      console.log(`[Realtime] Active channels: ${currentChannels.length}`, 
        currentChannels.map(c => ({ 
          name: c.topic, 
          state: c.state
        }))
      );
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, [initialized]);
  
  return null;
};

export default RealtimeInitializer;
